import React, { useState, useEffect, useCallback } from "react";
import type { ReactNode } from "react";
import {
  Activity,
  CheckCircle,
  Clock,
  Server,
  Wrench,
  BookOpen,
  MessageSquare,
  AlertTriangle,
  RefreshCw,
  Zap,
  Share2,
  GraduationCap,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import "../AgenticGateway.css";
import { useToast } from "../../../../hooks/use-toast";
import { GatewayDashboardApi, GatewayA2AApi } from "../Api/gatewayApi";
import { HealthData, ServerHealth } from "../interfaces/gatewayInterfaces";
import {
  formatDuration,
  formatTimestamp,
  timeAgo,
  successRateColor,
  apiError,
} from "../utils/gatewayUtils";
import GatewayStatCard from "../components/GatewayStatCard";
import { Button } from "../../../ui/button";
import { Skeleton } from "../../../ui/skeleton";

const REFRESH_INTERVAL_MS = 30000;

function HealthCardHeader({
  icon: Icon,
  title,
  description,
  detail,
  tone,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  detail?: ReactNode;
  tone?: string;
}) {
  return (
    <div className="ag-card-header">
      <div className="ag-card-heading" data-tone={tone}>
        <span className="ag-heading-icon" aria-hidden="true">
          <Icon />
        </span>
        <div className="min-w-0">
          <h2 className="ag-card-title">{title}</h2>
          <p className="ag-card-description">{description}</p>
        </div>
      </div>
      {detail != null && <span className="ag-card-detail">{detail}</span>}
    </div>
  );
}

function HealthListRow({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon?: LucideIcon;
  label: string;
  value: ReactNode;
  tone?: string;
}) {
  return (
    <div className="ag-list-row" data-tone={tone ?? "muted"}>
      {Icon && (
        <span className="ag-list-icon" aria-hidden="true">
          <Icon />
        </span>
      )}
      <span className="ag-list-label">{label}</span>
      <span className="ag-list-value" data-tone={tone}>{value}</span>
    </div>
  );
}

function ServerHealthCard({ server }: { server: ServerHealth }) {
  const isActive = server.active;
  const uptimeLabel = server.connectedAt
    ? timeAgo(server.connectedAt)
    : server.uptime
      ? formatUptime(server.uptime)
      : "-";
  const lastActivity = server.lastActivity ? formatTimestamp(server.lastActivity) : "No activity";

  return (
    <div className="ag-server" data-active={isActive ? "true" : "false"}>
      <div className="ag-server-head">
        <span className="ag-server-icon" aria-hidden="true">
          <Server />
          <span className="ag-server-dot" data-tone={isActive ? "success" : "muted"} />
        </span>
        <span className="ag-server-name" title={server.name}>{server.name}</span>
        <span className="ag-chip" data-tone={isActive ? "success" : "muted"}>
          {isActive ? "Active" : "Inactive"}
        </span>
      </div>

      <div className="ag-server-metrics">
        <div className="ag-metric">
          <span className="ag-metric-label">Uptime</span>
          <span className="ag-metric-value" title={uptimeLabel}>{uptimeLabel}</span>
        </div>
        <div className="ag-metric" data-tone="primary">
          <span className="ag-metric-label">Tools</span>
          <span className="ag-metric-value">{server.toolCount ?? 0}</span>
        </div>
        <div className="ag-metric" data-tone="violet">
          <span className="ag-metric-label">Resources</span>
          <span className="ag-metric-value">{server.resourceCount ?? 0}</span>
        </div>
        <div className="ag-metric" data-tone="amber">
          <span className="ag-metric-label">Prompts</span>
          <span className="ag-metric-value">{server.promptCount ?? 0}</span>
        </div>
      </div>

      <div className="ag-server-foot">
        <span>
          <Clock aria-hidden="true" />
          Last activity
        </span>
        <strong>{lastActivity}</strong>
      </div>
    </div>
  );
}

function formatUptime(ms: number): string {
  if (!ms || ms <= 0) return "-";
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
}

function SkeletonCardHeader() {
  return (
    <div className="ag-card-header">
      <div className="ag-card-heading">
        <Skeleton className="ag-skeleton h-[38px] w-[38px] rounded-xl" />
        <div className="space-y-2">
          <Skeleton className="ag-skeleton h-3.5 w-32" />
          <Skeleton className="ag-skeleton h-3 w-44" />
        </div>
      </div>
    </div>
  );
}

function HealthPageSkeleton() {
  return (
    <>
      <div className="ag-stat-grid">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="ag-stat">
            <Skeleton className="ag-skeleton h-[42px] w-[42px] shrink-0 rounded-xl" />
            <div className="flex-1 space-y-2">
              <Skeleton className="ag-skeleton h-5 w-16" />
              <Skeleton className="ag-skeleton h-3 w-24" />
            </div>
          </div>
        ))}
      </div>
      <div className="ag-health-layout">
        <section className="ag-card ag-health-servers">
          <SkeletonCardHeader />
          <div className="ag-card-body ag-server-grid">
            {[0, 1].map((i) => (
              <div key={i} className="ag-server">
                <div className="ag-server-head">
                  <Skeleton className="ag-skeleton h-[38px] w-[38px] shrink-0 rounded-xl" />
                  <Skeleton className="ag-skeleton h-4 flex-1" />
                </div>
                <div className="ag-server-metrics">
                  {[0, 1, 2, 3].map((j) => (
                    <Skeleton key={j} className="ag-skeleton h-[52px] rounded-[10px]" />
                  ))}
                </div>
                <Skeleton className="ag-skeleton h-3 w-2/3" />
              </div>
            ))}
          </div>
        </section>
        <div className="ag-side-stack">
          {[0, 1, 2].map((i) => (
            <section key={i} className="ag-card">
              <SkeletonCardHeader />
              <div className="ag-card-body space-y-3">
                {[0, 1, 2].map((j) => (
                  <Skeleton key={j} className="ag-skeleton h-4" />
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </>
  );
}

export default function GatewayHealthPage() {
  const { toast } = useToast();
  const [data, setData] = useState<HealthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const fetchHealth = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
      const result = await GatewayDashboardApi.health();
      if (result && (result.totalA2aAgents == null || result.totalSkills == null)) {
        const [a2aAgents, a2aSkills] = await Promise.all([
          GatewayA2AApi.list().catch(() => []),
          GatewayA2AApi.skills().catch(() => []),
        ]);
        if (result.totalA2aAgents == null) result.totalA2aAgents = (a2aAgents || []).length;
        if (result.totalSkills == null) result.totalSkills = (a2aSkills || []).length;
      }
      setData(result);
      setLastRefreshed(new Date());
    } catch (e: any) {
      toast({
        title: "Failed to load health data",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchHealth(true);
    const timer = setInterval(() => fetchHealth(false), REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [fetchHealth]);

  const successRate = data?.successRate ?? 0;
  const successColorClass = successRateColor(successRate);

  const activeServers = data?.activeServers ?? 0;
  const totalServers = data?.servers?.length ?? 0;

  const lat = data?.latency ?? { p50: undefined, p95: undefined, p99: undefined };

  const latencyTone = { p50: "success", p95: "amber", p99: "danger" } as const;

  if (loading) {
    return (
      <div className="ag-page" aria-busy="true">
        <div className="ag-card ag-toolbar">
          <span className="ag-live" data-tone="primary">
            <span className="ag-spinner ag-spinner-sm animate-spin" aria-hidden="true" />
            Loading health data…
          </span>
        </div>
        <HealthPageSkeleton />
      </div>
    );
  }

  return (
    <div className="ag-page">
      <div className="ag-card ag-toolbar">
        <div className="ag-toolbar-group">
          <span className="ag-live" data-tone="success">
            <span className="ag-live-dot" aria-hidden="true" />
            Live monitoring
          </span>
          <span className="ag-meta">
            <RefreshCw aria-hidden="true" />
            Auto-refresh every {REFRESH_INTERVAL_MS / 1000}s
          </span>
          {lastRefreshed && (
            <span className="ag-meta">
              <Clock aria-hidden="true" />
              Updated {lastRefreshed.toLocaleTimeString()}
            </span>
          )}
        </div>
        <Button
          variant="outline"
          onClick={() => fetchHealth(true)}
          className="ag-outline-button no-default-hover-elevate"
        >
          <RefreshCw />
          Refresh
        </Button>
      </div>

      <div className="ag-stat-grid">
        <GatewayStatCard
          icon={<CheckCircle />}
          label="Success Rate (24h)"
          value={`${successRate}%`}
          colorClass={successColorClass}
        />
        <GatewayStatCard
          icon={<Activity />}
          label="Requests / min"
          value={data?.requestsPerMinute ?? 0}
          colorClass="text-primary"
        />
        <GatewayStatCard
          icon={<Clock />}
          label="Avg Latency (24h)"
          value={formatDuration(data?.avgDurationMs ?? data?.avgLatency)}
          colorClass="text-amber-500"
        />
        <GatewayStatCard
          icon={<Server />}
          label="Active Servers"
          value={`${activeServers} / ${totalServers}`}
          colorClass="text-violet-500"
        />
        <GatewayStatCard
          icon={<Share2 />}
          label="A2A Agents"
          value={data?.totalA2aAgents ?? 0}
          colorClass="text-fuchsia-500"
        />
      </div>

      <div className="ag-health-layout">
        <section className="ag-card ag-health-servers">
          <HealthCardHeader
            icon={Server}
            title="Server Health"
            description="Connection status and capabilities of each MCP server"
            detail={`${activeServers} / ${totalServers} active`}
          />
          <div className="ag-card-body ag-health-scroll">
            {!data?.servers || data.servers.length === 0 ? (
              <div className="ag-empty">
                <span className="ag-empty-icon" aria-hidden="true">
                  <Server />
                </span>
                <p>No servers configured</p>
              </div>
            ) : (
              <div className="ag-server-grid">
                {data.servers.map((server) => (
                  <ServerHealthCard key={server.name ?? server.configKey} server={server} />
                ))}
              </div>
            )}
          </div>
        </section>

        <div className="ag-side-stack">
          <section className="ag-card">
            <HealthCardHeader
              icon={Zap}
              title="Latency Percentiles"
              description="Response time distribution"
              detail="24h"
              tone="amber"
            />
            <div className="ag-card-body ag-latency">
              {(["p50", "p95", "p99"] as const).map((pct) => (
                <div key={pct} className="ag-latency-row" data-tone={latencyTone[pct]}>
                  <span className="ag-latency-label">{pct}</span>
                  <div className="ag-latency-track">
                    <div
                      className="ag-latency-fill"
                      style={{
                        width:
                          lat[pct]
                            ? `${Math.min(100, (lat[pct]! / (lat.p99 || 1)) * 100)}%`
                            : "0%",
                      }}
                    />
                  </div>
                  <span className="ag-latency-value">{formatDuration(lat[pct])}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="ag-card">
            <HealthCardHeader
              icon={Wrench}
              title="Capability Inventory"
              description="Capabilities exposed through the gateway"
            />
            <div className="ag-card-body ag-list">
              <HealthListRow icon={Wrench} label="Tools" value={data?.totalTools ?? 0} tone="primary" />
              <HealthListRow icon={BookOpen} label="Resources" value={data?.totalResources ?? 0} tone="violet" />
              <HealthListRow icon={MessageSquare} label="Prompts" value={data?.totalPrompts ?? 0} tone="amber" />
              <HealthListRow icon={GraduationCap} label="Skills" value={data?.totalSkills ?? 0} tone="teal" />
            </div>
          </section>

          <section className="ag-card">
            <HealthCardHeader
              icon={Activity}
              title="Activity"
              description="Gateway events in the last 24 hours"
              detail="24h"
              tone="success"
            />
            <div className="ag-card-body ag-list">
              <HealthListRow
                icon={Activity}
                label="Total Events"
                value={data?.recentEventCount ?? data?.totalEvents ?? 0}
              />
              <HealthListRow
                icon={AlertTriangle}
                label="Errors"
                value={data?.recentErrorCount ?? data?.totalErrors ?? 0}
                tone="danger"
              />
              <HealthListRow icon={Zap} label="In-Flight" value={data?.inFlightCount ?? 0} tone="primary" />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
